import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Calculator, Info } from "lucide-react";
import { useToast } from "../../Context/ToastContext";
import { calculateCtcStructure, fetchCtcPlannerPresets } from "./ctcPlannerApi";

function fmt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function MoneyRow({ label, value, hint }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
      <div>
        <p className="text-sm text-slate-700">{label}</p>
        {hint ? <p className="text-xs text-slate-500 mt-0.5">{hint}</p> : null}
      </div>
      <p className="text-sm font-semibold text-slate-900 tabular-nums">₹ {fmt(value)}</p>
    </div>
  );
}

export default function CtcSalaryPlanner() {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    annual_ctc: searchParams.get("ctc") || "600000",
    preset: "standard_40",
    metro: true,
    include_gratuity_accrual: false,
    basic_percent_of_monthly_ctc: "",
    hra_percent_of_basic: "",
  });

  useEffect(() => {
    fetchCtcPlannerPresets()
      .then(setPresets)
      .catch(() => toast.error("Could not load CTC presets"));
  }, [toast]);

  const selectedPreset = useMemo(
    () => presets.find((p) => p.id === form.preset),
    [presets, form.preset]
  );

  const runCalculate = useCallback(async () => {
    const annual = Number(form.annual_ctc);
    if (!Number.isFinite(annual) || annual <= 0) {
      toast.error("Enter a valid annual CTC");
      return;
    }
    try {
      setLoading(true);
      const body = {
        annual_ctc: annual,
        preset: form.preset,
        metro: form.metro,
        include_gratuity_accrual: form.include_gratuity_accrual,
      };
      if (form.basic_percent_of_monthly_ctc !== "") {
        body.basic_percent_of_monthly_ctc = Number(form.basic_percent_of_monthly_ctc);
      }
      if (form.hra_percent_of_basic !== "") {
        body.hra_percent_of_basic = Number(form.hra_percent_of_basic);
      }
      const res = await calculateCtcStructure(body);
      setResult(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Calculation failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [form, toast]);

  useEffect(() => {
    if (searchParams.get("ctc")) {
      runCalculate();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- initial deep-link only

  return (
    <div className="bw-page w-full max-w-full min-w-0">
      <header className="bw-page-header mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
            <Calculator size={22} />
          </div>
          <div>
            <h1 className="bw-page-title">CTC Salary Planner</h1>
            <p className="bw-page-subtitle">
              Enter annual CTC to auto-split Basic, HRA, Special Allowance, PF &amp; ESI. Standalone
              tool — does not change payroll runs or employee assignments.
            </p>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bw-panel">
          <h2 className="bw-section-title">Input</h2>
          <div className="bw-stack">
            <div className="bw-field">
              <label className="bw-label">Annual CTC (₹)</label>
              <input
                type="number"
                className="bw-input"
                min="1"
                step="1000"
                value={form.annual_ctc}
                onChange={(e) => setForm((p) => ({ ...p, annual_ctc: e.target.value }))}
              />
              {form.annual_ctc && Number(form.annual_ctc) > 0 && (
                <p className="text-xs text-emerald-600 mt-1">
                  ≈ ₹{(Number(form.annual_ctc) / 100000).toFixed(2)} LPA · ₹ {fmt(Number(form.annual_ctc) / 12)} / month
                </p>
              )}
            </div>

            <div className="bw-field">
              <label className="bw-label">Structure preset</label>
              <select
                className="bw-input"
                value={form.preset}
                onChange={(e) => setForm((p) => ({ ...p, preset: e.target.value }))}
              >
                {presets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
                {!presets.length && (
                  <>
                    <option value="standard_40">Standard (40% Basic)</option>
                    <option value="compliance_50">Compliance-heavy (50% Basic)</option>
                    <option value="startup_flexible">Startup (35% Basic)</option>
                  </>
                )}
              </select>
              {selectedPreset?.description && (
                <p className="text-xs text-slate-500 mt-1">{selectedPreset.description}</p>
              )}
            </div>

            <div className="bw-form-grid">
              <div className="bw-field">
                <label className="bw-label">Basic % override (optional)</label>
                <input
                  type="number"
                  className="bw-input"
                  placeholder={selectedPreset ? String(selectedPreset.basic_percent_of_monthly_ctc) : "40"}
                  value={form.basic_percent_of_monthly_ctc}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, basic_percent_of_monthly_ctc: e.target.value }))
                  }
                />
              </div>
              <div className="bw-field">
                <label className="bw-label">HRA % of Basic override (optional)</label>
                <input
                  type="number"
                  className="bw-input"
                  placeholder={selectedPreset ? String(selectedPreset.hra_percent_of_basic) : "50"}
                  value={form.hra_percent_of_basic}
                  onChange={(e) => setForm((p) => ({ ...p, hra_percent_of_basic: e.target.value }))}
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.metro}
                onChange={(e) => setForm((p) => ({ ...p, metro: e.target.checked }))}
              />
              Metro city (higher HRA on Basic)
            </label>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.include_gratuity_accrual}
                onChange={(e) =>
                  setForm((p) => ({ ...p, include_gratuity_accrual: e.target.checked }))
                }
              />
              Include gratuity accrual (4.81% of Basic) in employer cost
            </label>

            <button
              type="button"
              className="bw-btn bw-btn-primary"
              disabled={loading}
              onClick={runCalculate}
            >
              {loading ? "Calculating…" : "Calculate breakdown"}
            </button>
          </div>
        </section>

        <section className="bw-panel">
          <h2 className="bw-section-title">Monthly breakdown</h2>
          {!result ? (
            <p className="text-sm text-slate-500">Enter CTC and calculate to see the split.</p>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-emerald-50 p-3">
                  <p className="text-xs text-emerald-800">Gross (monthly)</p>
                  <p className="text-lg font-semibold text-emerald-900">
                    ₹ {fmt(result.earnings?.gross)}
                  </p>
                </div>
                <div className="rounded-lg bg-blue-50 p-3">
                  <p className="text-xs text-blue-800">Net take-home</p>
                  <p className="text-lg font-semibold text-blue-900">
                    ₹ {fmt(result.summary?.net_take_home)}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  Earnings
                </h3>
                <MoneyRow label="Basic" value={result.earnings?.basic} />
                <MoneyRow label="HRA" value={result.earnings?.hra} />
                <MoneyRow label="Special allowance" value={result.earnings?.special_allowance} />
              </div>

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  Employee deductions
                </h3>
                <MoneyRow label="PF (employee)" value={result.employee_deductions?.pf} />
                <MoneyRow
                  label="ESI (employee)"
                  value={result.employee_deductions?.esi}
                  hint={result.esi_detail?.eligible ? "ESI applicable" : "Not applicable"}
                />
              </div>

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  Employer cost (in CTC)
                </h3>
                <MoneyRow label="PF (employer)" value={result.employer_contributions?.pf} />
                <MoneyRow label="ESI (employer)" value={result.employer_contributions?.esi} />
                {result.employer_contributions?.gratuity_accrual > 0 && (
                  <MoneyRow
                    label="Gratuity accrual"
                    value={result.employer_contributions?.gratuity_accrual}
                  />
                )}
              </div>

              {result.notes?.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-3 flex gap-2">
                  <Info size={16} className="text-amber-700 shrink-0 mt-0.5" />
                  <ul className="text-xs text-amber-900 space-y-1 list-disc list-inside">
                    {result.notes.map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
